'use client';

import { alpha, Box, Button, Theme } from '@mui/material';

interface TradeButtonsProps {
  onYesClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onNoClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onDrawClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  yesLabel?: string;
  noLabel?: string;
  drawLabel?: string;
  yesColor?: string;
  noColor?: string;
  truncateLabel?: boolean;
}

function getYesStyles(theme: Theme) {
  return {
    py: 1.25,
    flex: 1,
    minWidth: 0,
    borderRadius: '12px',
    textTransform: 'none' as const,
    fontWeight: 700,
    fontSize: '1rem',
    bgcolor: theme.palette.success.bg,
    color: theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.dark,
    border: '1px solid',
    borderColor: theme.palette.success.border,
    boxShadow: 'none',
    '&:hover': {
      bgcolor: alpha(theme.palette.success.main, 0.25),
      boxShadow: 'none',
    },
  };
}

function getNoStyles(theme: Theme) {
  return {
    py: 1.25,
    flex: 1,
    minWidth: 0,
    borderRadius: '12px',
    textTransform: 'none' as const,
    fontWeight: 700,
    fontSize: '1rem',
    bgcolor: theme.palette.error.bg,
    color: theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.dark,
    border: '1px solid',
    borderColor: theme.palette.error.border,
    boxShadow: 'none',
    '&:hover': {
      bgcolor: alpha(theme.palette.error.main, 0.25),
      boxShadow: 'none',
    },
  };
}

function getDrawStyles(theme: Theme) {
  return {
    py: 1.25,
    flex: 1,
    minWidth: 0,
    borderRadius: '12px',
    textTransform: 'none' as const,
    fontWeight: 700,
    fontSize: '1rem',
    bgcolor: theme.palette.background.subtle,
    color: theme.palette.text.secondary,
    border: '1px solid',
    borderColor: theme.palette.mode === 'dark' ? 'white' : theme.palette.divider,
    boxShadow: 'none',
    '&:hover': {
      bgcolor: theme.palette.background.hover,
      borderColor: theme.palette.text.disabled,
      boxShadow: 'none',
    },
  };
}

export function TradeButtons({
  onYesClick,
  onNoClick,
  onDrawClick,
  yesLabel = 'Yes',
  noLabel = 'No',
  drawLabel,
  yesColor,
  noColor,
  truncateLabel = false,
}: TradeButtonsProps) {
  return (
    <Box sx={{ display: 'flex', width: '100%', gap: 1, mt: 1 }}>
      <Button
        variant="contained"
        onClick={onYesClick}
        sx={(theme) => ({
          ...getYesStyles(theme),
          ...(truncateLabel && {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
          }),
          ...(yesColor && {
            bgcolor: yesColor,
            borderColor: yesColor,
            color: '#fff',
            '&:hover': {
              bgcolor: alpha(yesColor, 0.8),
              boxShadow: 'none',
            },
          }),
        })}
      >
        {yesLabel}
      </Button>
      {drawLabel && onDrawClick && (
        <Button
          variant="contained"
          onClick={onDrawClick}
          sx={(theme) => ({
            ...getDrawStyles(theme),
            ...(truncateLabel && {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
            }),
          })}
        >
          {drawLabel}
        </Button>
      )}
      <Button
        variant="contained"
        onClick={onNoClick}
        sx={(theme) => ({
          ...getNoStyles(theme),
          ...(truncateLabel && {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
          }),
          ...(noColor && {
            bgcolor: noColor,
            borderColor: noColor,
            color: '#fff',
            '&:hover': {
              bgcolor: alpha(noColor, 0.8),
              boxShadow: 'none',
            },
          }),
        })}
      >
        {noLabel}
      </Button>
    </Box>
  );
}
